import { useMemo, useState } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip,
  Checkbox, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Typography, Divider,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { colors } from "../../../../theme";
import { allVulnerabilitiesForAsset, vulnSeverityTier } from "./assetUtils";
import { ZONE_RISK_LEVELS, zoneRiskLabel } from "./zoneUtils";
import { computeSecurityScore } from "./securityScore";

const ScoreCircle = ({ score, color, label }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.8 }}>
    <Box
      sx={{
        width: 76, height: 76, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: `4px solid ${color}`, color,
      }}
    >
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{score}</Typography>
    </Box>
    <Typography sx={{ fontSize: 11, color: colors.grey[400], textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

const BreakdownDiffRow = ({ label, before, after, max }) => {
  const pctBefore = max > 0 ? Math.round((before / max) * 100) : 0;
  const pctAfter = max > 0 ? Math.round((after / max) * 100) : 0;
  const improved = pctAfter < pctBefore;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
      <Typography sx={{ fontSize: 11.5, color: colors.grey[300], minWidth: 100 }}>{label}</Typography>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: "#161616", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, width: `${pctBefore}%`, backgroundColor: colors.grey[700] }} />
        <Box sx={{ position: "absolute", inset: 0, width: `${pctAfter}%`, backgroundColor: improved ? "#4cceac" : "#ff7744", opacity: 0.85 }} />
      </Box>
      <Typography sx={{ fontSize: 11, color: colors.grey[500], minWidth: 70, textAlign: "right" }}>
        {pctBefore}% → {pctAfter}%
      </Typography>
    </Box>
  );
};

const ImpactStat = ({ label, value }) => (
  <Box sx={{ textAlign: "center", flex: "1 1 100px" }}>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: value > 0 ? "#4cceac" : colors.grey[600], fontFamily: "monospace" }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 10, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
  </Box>
);

// "What if" score simulator — purely client-side, nothing is persisted. Lets the
// user toggle common remediation actions (acknowledge CVEs by tier, re-assess or
// clear zone risk, retire offline assets) and see the recomputed score plus a
// concrete impact summary for the same slice currently shown in the Overview
// (whole network, or a filtered zone/vlan/vendor/type — via `scopeLabel`),
// without touching any real data.
const ScoreSimulatorDialog = ({ open, onClose, rows, zones, acks, currentScore, scopeLabel }) => {
  const [ackCritical, setAckCritical] = useState(false);
  const [ackHigh, setAckHigh] = useState(false);
  const [ackMediumLow, setAckMediumLow] = useState(false);
  const [zoneRiskOverride, setZoneRiskOverride] = useState("");
  const [clearZoneRisk, setClearZoneRisk] = useState(false);
  const [retireOffline, setRetireOffline] = useState(false);

  const result = useMemo(() => {
    const baseRows = retireOffline ? rows.filter((r) => r.status === "online") : rows;

    // clearZoneRisk (drop every zone from scoring entirely) takes precedence over
    // just re-leveling risk — they'd otherwise fight over the same input.
    const simZones = clearZoneRisk
      ? []
      : zoneRiskOverride
        ? zones.map((z) => ({ ...z, risk: zoneRiskOverride }))
        : zones;

    const syntheticAcks = [];
    const ackCounts = { critical: 0, high: 0, medium: 0, low: 0 };

    baseRows.forEach((row) => {
      allVulnerabilitiesForAsset(row).forEach((v) => {
        const tier = vulnSeverityTier(v);
        const matches =
          (ackCritical && tier === "critical") ||
          (ackHigh && tier === "high") ||
          (ackMediumLow && (tier === "medium" || tier === "low"));
        if (matches) {
          syntheticAcks.push({ asset_id: row.id, cve_id: v.cve_id, origin: v._originKey });
          if (tier in ackCounts) ackCounts[tier]++;
        }
      });
    });

    const score = computeSecurityScore({ rows: baseRows, zones: simZones, acks: [...acks, ...syntheticAcks] });
    const retiredCount = rows.length - baseRows.length;
    const zonesAffected = clearZoneRisk ? zones.length : zoneRiskOverride ? zones.filter((z) => z.risk !== zoneRiskOverride).length : 0;

    return { score, ackCounts, retiredCount, zonesAffected };
  }, [rows, zones, acks, ackCritical, ackHigh, ackMediumLow, zoneRiskOverride, clearZoneRisk, retireOffline]);

  const simulated = result.score;
  const delta = simulated.score - currentScore.score;
  const anyChange = ackCritical || ackHigh || ackMediumLow || zoneRiskOverride || clearZoneRisk || retireOffline;
  const totalAcked = result.ackCounts.critical + result.ackCounts.high + result.ackCounts.medium + result.ackCounts.low;

  const handleReset = () => {
    setAckCritical(false);
    setAckHigh(false);
    setAckMediumLow(false);
    setZoneRiskOverride("");
    setClearZoneRisk(false);
    setRetireOffline(false);
  };

  // One-click "ceiling" — everything remediated at once, to see the best achievable
  // score for this slice.
  const handleBestCase = () => {
    setAckCritical(true);
    setAckHigh(true);
    setAckMediumLow(true);
    setClearZoneRisk(true);
    setZoneRiskOverride("");
    setRetireOffline(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
        <ScienceIcon sx={{ color: "#459095", fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Simulate Score Changes</Typography>
          <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
            Nothing here is saved — this is a "what if" preview for: <b>{scopeLabel}</b>
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AutoAwesomeIcon sx={{ fontSize: "15px !important" }} />}
          onClick={handleBestCase}
          sx={{ ml: "auto", color: "#4cceac", textTransform: "none", fontSize: 11.5 }}
        >
          Best case
        </Button>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161616", pt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* The chip is absolutely positioned so it's out of normal flow entirely —
            the score circles center on the container's width alone and never
            shift, no matter how wide "No changes" vs. "+N pts" ends up being. */}
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 3, minHeight: 76 }}>
          <ScoreCircle score={currentScore.score} color={currentScore.color} label="Current" />
          <ArrowForwardIcon sx={{ color: colors.grey[600] }} />
          <ScoreCircle score={simulated.score} color={simulated.color} label="Simulated" />
          <Box sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
            <Chip
              label={anyChange ? `${delta >= 0 ? "+" : ""}${delta} pts` : "No changes"}
              sx={{
                bgcolor: !anyChange ? "#88888822" : delta > 0 ? "#4cceac22" : delta < 0 ? "#ff444422" : "#88888822",
                color: !anyChange ? colors.grey[500] : delta > 0 ? "#4cceac" : delta < 0 ? "#ff6644" : colors.grey[400],
                fontWeight: 700,
              }}
            />
          </Box>
        </Box>

        {/* Always mounted at a fixed height (never appears/disappears) — shows
            zeroed/muted stats until an option is toggled, so nothing below it jumps. */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 1.2, borderRadius: 1, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}` }}>
          <ImpactStat label="CVEs Acknowledged" value={totalAcked} />
          <ImpactStat label="Zones Changed" value={result.zonesAffected} />
          <ImpactStat label="Assets Retired" value={result.retiredCount} />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
              Remediation actions
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={ackCritical} onChange={(e) => setAckCritical(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 12.5 }}>Acknowledge all Critical CVEs</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={ackHigh} onChange={(e) => setAckHigh(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 12.5 }}>Acknowledge all High CVEs</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={ackMediumLow} onChange={(e) => setAckMediumLow(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 12.5 }}>Acknowledge all Medium/Low CVEs</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={retireOffline} onChange={(e) => setRetireOffline(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 12.5 }}>Retire offline assets (exclude from scoring)</Typography>}
              />
            </Box>

            <Divider sx={{ my: 1.5, borderColor: colors.grey[800] }} />

            <FormControlLabel
              control={<Checkbox size="small" checked={clearZoneRisk} onChange={(e) => { setClearZoneRisk(e.target.checked); if (e.target.checked) setZoneRiskOverride(""); }} />}
              label={<Typography sx={{ fontSize: 12.5 }}>Clear all zone risk (unassign entirely)</Typography>}
            />
            <FormControl size="small" fullWidth sx={{ mt: 1, opacity: clearZoneRisk ? 0.5 : 1 }} disabled={clearZoneRisk}>
              <InputLabel>Or set all zone risk to</InputLabel>
              <Select
                label="Or set all zone risk to"
                value={zoneRiskOverride}
                onChange={(e) => setZoneRiskOverride(e.target.value)}
              >
                <MenuItem value="">No change</MenuItem>
                {ZONE_RISK_LEVELS.map((r) => (
                  <MenuItem key={r} value={r}>{zoneRiskLabel(r)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
              Score breakdown — before vs. after
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              {currentScore.breakdown.map((b, i) => (
                <BreakdownDiffRow
                  key={b.key}
                  label={b.label}
                  before={b.penalty}
                  after={simulated.breakdown[i]?.penalty ?? b.penalty}
                  max={b.max}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}` }}>
        <Button onClick={handleReset} disabled={!anyChange}>Reset</Button>
        <Button onClick={onClose} variant="contained" disableElevation>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScoreSimulatorDialog;
